import { HST_ERROR_CODES, type HogesErrorType } from "@infinitaslearning/module-he-common";
import type React from "react";
import { Trans, useTranslation } from "react-i18next";

import type { UsersAPI } from "generated/types";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useLocaleDateFormatter } from "hooks/useLocaleDateFormatter";

import { FeedbackMessage } from "components/FeedbackMessage/FeedbackMessage";

export enum ClaimInviteLicenseOutcome {
  Success = "SUCCESS",
  GenericError = "UNKNOWN-ERROR",
}

type ClaimInviteLicenseOutcomeType = ClaimInviteLicenseOutcome | HogesErrorType;

interface ClaimInviteResultProps {
  outcomeType: ClaimInviteLicenseOutcomeType;
  licenseInfo: UsersAPI.InviteLinkLicenseInfo;
}

export const ClaimInviteResult: React.FC<ClaimInviteResultProps> = ({
  licenseInfo,
  outcomeType,
}) => {
  const { t } = useTranslation("homepage");
  const { formatDate } = useLocaleDateFormatter();
  const { getCustomSupportEmailByDomain } = useDomainHandler();

  switch (outcomeType) {
    case ClaimInviteLicenseOutcome.Success:
      return (
        <FeedbackMessage
          dataCy="license-claimed-message"
          variant="success"
          title={t("homepage:claimInviteLinkLicensePage.claimResult.success.title")}
          description={
            <Trans
              components={{
                productNameTag: <span data-cy="claimed-product-name" />,
                expirationDateTag: <span data-cy="claimed-product-expiration-date" />,
              }}
              i18nKey="homepage:claimInviteLinkLicensePage.claimResult.success.description"
              t={t}
              values={{
                productName: licenseInfo.productNames,
                expirationDate: formatDate(licenseInfo.licenseExpirationDate),
              }}
            />
          }
        />
      );
    case HST_ERROR_CODES.EXPIRED_INVITE_LINK:
      return (
        <FeedbackMessage
          dataCy="expired-license-message"
          variant="error"
          title={t("homepage:claimInviteLinkLicensePage.claimResult.expiredInviteLink.title")}
          description={t(
            "homepage:claimInviteLinkLicensePage.claimResult.expiredInviteLink.description",
          )}
        />
      );
    case HST_ERROR_CODES.USER_EDUCATION_MISMATCH:
      return (
        <FeedbackMessage
          dataCy="education-mismatch-message"
          variant="warning"
          title={t("homepage:claimInviteLinkLicensePage.claimResult.userEducationMismatch.title")}
          description={t(
            "homepage:claimInviteLinkLicensePage.claimResult.userEducationMismatch.description",
          )}
        />
      );
    case HST_ERROR_CODES.INVITE_LINK_ALREADY_USED:
      return (
        <FeedbackMessage
          dataCy="already-claimed-message"
          variant="warning"
          title={t("homepage:claimInviteLinkLicensePage.claimResult.alreadyClaimed.title")}
          description={t(
            "homepage:claimInviteLinkLicensePage.claimResult.alreadyClaimed.description",
          )}
        />
      );
    case HST_ERROR_CODES.INVITE_LINK_MAX_USAGE_REACHED:
      return (
        <FeedbackMessage
          dataCy="max-usage-reached-message"
          variant="error"
          title={t("homepage:claimInviteLinkLicensePage.claimResult.maxUsageReached.title")}
          description={t(
            "homepage:claimInviteLinkLicensePage.claimResult.maxUsageReached.description",
          )}
        />
      );
    default:
      return (
        <FeedbackMessage
          dataCy="unknown-error-message"
          variant="error"
          title={t("homepage:claimInviteLinkLicensePage.claimResult.unknownError.title")}
          description={
            <Trans
              components={{
                // biome-ignore lint/a11y/useAnchorContent: Link has text, but it's defined in the translations
                supportLink: <a href={`mailto:${getCustomSupportEmailByDomain()}`} />,
              }}
              i18nKey="homepage:claimInviteLinkLicensePage.claimResult.unknownError.description"
              t={t}
            />
          }
        />
      );
  }
};
